import api from "./api";
import { listQueue, removeItem, bumpAttempts, queueCount } from "./offlineQueue";

let syncing = false;
const listeners = new Set();

export function onSyncChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function notify() {
  const count = await queueCount();
  listeners.forEach((fn) => fn({ online: navigator.onLine, count, syncing }));
}

async function uploadBlob(blob, filename) {
  const fd = new FormData();
  const file = new File([blob], filename || "photo.jpg", { type: blob.type || "image/jpeg" });
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.id;
}

export async function flushQueue() {
  if (syncing || !navigator.onLine) return;
  syncing = true;  // Set BEFORE any await to prevent concurrent flushes
  try {
    await notify();
    const items = await listQueue();
    for (const item of items) {
      try {
        if (item.kind === "attendance") {
          const p = { ...item.payload };
          if (p._photo_blob && !p.photo_id) {
            try { p.photo_id = await uploadBlob(p._photo_blob, p._photo_name); } catch { /* keep null */ }
          }
          delete p._photo_blob; delete p._photo_name;
          await api.post("/attendance", p);
          await removeItem(item.id);
        } else if (item.kind === "expense") {
          const p = { ...item.payload };
          if (p._receipt_blob && !p.receipt_path) {
            try { p.receipt_path = await uploadBlob(p._receipt_blob, p._receipt_name); } catch { /* keep null */ }
          }
          delete p._receipt_blob; delete p._receipt_name;
          await api.post("/expenses", p);
          await removeItem(item.id);
        } else {
          await removeItem(item.id);
        }
      } catch (err) {
        // 4xx (validation/permission) -> drop after 3 attempts; 5xx/network -> retry next time
        const status = err?.response?.status;
        if (status && status >= 400 && status < 500) {
          await bumpAttempts(item.id);
          const fresh = (await listQueue()).find((x) => x.id === item.id);
          if (fresh && (fresh.attempts || 0) >= 3) await removeItem(item.id);
        } else {
          await bumpAttempts(item.id);
        }
        // Continue with remaining items
      }
    }
  } finally {
    syncing = false;
    await notify();
  }
}

export function startSyncManager() {
  window.addEventListener("online", () => { flushQueue(); });
  window.addEventListener("offline", () => { notify(); });
  // Periodic retry every 60s when online and queue has items
  setInterval(async () => {
    if (navigator.onLine && (await queueCount()) > 0) flushQueue();
  }, 60000);
  // Initial flush attempt
  setTimeout(flushQueue, 2000);
  notify();
}

export async function getStatus() {
  return { online: navigator.onLine, count: await queueCount(), syncing };
}
